import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import store from 'store2';
import cryptoJS from 'crypto-js';
import { LoginTokenTypes, useAPICall, useAxiosAuthInstance, useDSIMAxiosInstance, useIsSessionValid } from '../../../Contexts/Axios';
import { useLogin, useUpdateUser, useUpdateUserProfile, useUserState } from '../../../Contexts/User';
import {
    useLocale,
    useLocalization,
    useLocalizationDispatch,
    useLocalizationHelpers,
    LocalizationTypes as LocaleDispatchTypes,
    Locales,
    LocalesFull,
} from '../../../Contexts/Localization';
import randomstring from 'randomstring';
import { APIError, DashboardUserLoginInfo, DashboardUserProfile, DealerPaymentAccount, LoggedInUser } from '../../../API/APIObjects';
import { DashboardUserErrors, LoginErrors, LoginLocation, PaymentProvider, PermissionLevel, UserPermissions } from '../../../API/Enums';
import { AppType, useAppCoreState } from '../../../Contexts/AppCore';
import css from './Login.module.scss';
import { GMSpinner } from '../../../CommonControls/Spinner/Spinner';
import LoginScreenLogo from '../../../Assets/Images/LoginScreenLogo.png';
import { GMCard, GMCardBody, GMCardFooter } from '../../../CommonControls/Card/Card';
import { Heading2Text } from '../../../CommonControls/Text/Text';
import { useSetMessage } from '../../../Contexts/Message';
import classNames from 'classnames';
import { LocalStorageKeys, SessionStorageKeys } from '../../../CommonUtils/Storage';
import { GMButtonList } from '../../../CommonControls/Buttons/ButtonList';
import { GMButton } from '../../../CommonControls/Buttons/Button';
import { JSXCase, JSXSwitch } from '../../../CommonControls/JSXSwitch/JSXSwitch';
import { GMFormTextField } from '../../../CommonControls/Inputs/TextField';
import { GMFormPasswordField } from '../../../CommonControls/Inputs/PasswordField';
import { UserFieldLengths } from '../../../API/FieldLengths';
import { nameof } from '../../../CommonUtils/Nameof';
import { GMAlert } from '../../../CommonControls/Alert/Alert';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { AxiosError } from 'axios';
import { useIsMobile } from '../../../CommonUtils/MediaQueries';
import { GMDivider } from '../../../CommonControls/Divider/Divider';
import { VALID_EMAIL_REGEX } from '../../../CommonUtils/Validation';
import { GMFormEmailField } from '../../../CommonControls/Inputs/EmailField';


interface LoginFormData {
    username: string;
    password: string;
    newPassword: string;
    confirmNewPassword: string;
    email: string;
}

enum ScreenType {
    Username = 0,
    Password,
    dSIMEnrollment,
    PasswordCreation
}

export const Login = () => {
    const login = useLogin();
    /**
     * Given to us by dlrSecured when they redirect back to us after a user logs in. Used to obtain a JWT from dlrSecured
     */
    const authorization = useRef<string>(window.location.hash.startsWith('#code=') ? window.location.hash.replace('#code=', '') : '');
    /**
     * Passed in from from the Support site when opening Dashboard from the sidebar. Will attempt to log the user in via dlrSecured
     * if they're already logged in through dlrSecured and have a valid login cookie
     */
    const ssoUser = useRef<string | null>(new URLSearchParams(window.location.search)
        .get('sso'));
    /**
     * Passed when returning from the online payments onboarding due to the onboarding link expiring
     */
    const activatePaymentAccount: boolean = new URLSearchParams(window.location.search)
        .get('activatePaymentAccount') === 'true';
    /**
     * Passed when returning from the online payments onboarding (either the user canceling the process or completing it)
     */
    const returnedFromOnboarding: boolean = new URLSearchParams(window.location.search)
        .get('returnFromOnboarding') === 'true';
    /**
     * Encrypted dealer key passed when returning from the online payments onboarding
     */
    const dealerKey: string | null = new URLSearchParams(window.location.search)
        .get('dealerKey');
    /**
     * Encrypted appointment key passed when the user uses a past due reminder notification link for a specific appointment.
     */
    const appointmentKey: string | null = new URLSearchParams(window.location.search)
        .get('appointmentKey');
    /**
     * Boolean parameter to indicate that the link is coming from a notification we sent and should use special redirect logic.
     */
    const fromNotification: boolean = new URLSearchParams(window.location.search)
        .get('fromNotification') === 'true';
    const fromROReminder: boolean = new URLSearchParams(window.location.search)
        .get('fromROReminder') === 'true';
    const codeVerifier = useRef<string>('');
    const dSIMInstance = useDSIMAxiosInstance();
    const setMessage = useSetMessage();
    const isSessionValid = useIsSessionValid();
    const Localization = useLocalization();
    const [errorMessage, setErrorMessage] = useState<LoginErrors | DashboardUserErrors>(LoginErrors.None);
    const [errorResponse, setErrorResponse] = useState<string | undefined>(undefined);
    const api = useAPICall();
    const { RunningAppType } = useAppCoreState();
    const LocalizationHelpers = useLocalizationHelpers();
    const updateUser = useUpdateUser();
    const updateUserProfile = useUpdateUserProfile();
    const { UserData } = useUserState();
    const authInstance = useAxiosAuthInstance();
    const [screenType, setScreenType] = useState<ScreenType>(ScreenType.Username);
    const locale = useLocale();
    const localeDispatch = useLocalizationDispatch();
    const [paymentAccount, setPaymentAccount] = useState<DealerPaymentAccount | undefined>(undefined);
    const [activating, setActivating] = useState<boolean>(false);
    const isMobile = useIsMobile();
    const formRef = useRef<FormikProps<LoginFormData>>(null);
    /**
     * Determines if we are showing the loading splash screen
     */
    const [loading, setLoading] = useState<boolean>(
        (store.session.get(SessionStorageKeys.Token) && store.session.get(SessionStorageKeys.LoginSession)) ||
        authorization.current !== '' ||
        ssoUser.current !== null ||
        RunningAppType === AppType.Support ||
        ((activatePaymentAccount || returnedFromOnboarding) && !!dealerKey),
    );
    const initialValues = useMemo<LoginFormData>(() => {
        return {
            username: '',
            password: '',
            newPassword: '',
            confirmNewPassword: '',
            email: '',
        };
    }, []);

    const generateCodeVerifier = useCallback(() => {
        if (store.session.get(SessionStorageKeys.CodeVerifier)) {
            codeVerifier.current = store.session.get(SessionStorageKeys.CodeVerifier);
        }
        else {
            codeVerifier.current = randomstring.generate({
                length: 64,
                charset: 'hex',
            });
            store.session.set(SessionStorageKeys.CodeVerifier, codeVerifier.current);
        }
    }, []);

    const clearSessionStore = useCallback(() => {
        store.session.clear();
        generateCodeVerifier();
    }, [generateCodeVerifier]);

    const redirectToDSIM = useCallback((dSIMUserId: string = '', prompt: string = 'login') => {
        const hash = cryptoJS.SHA256(codeVerifier.current);

        /**
         * Encode the URL query parameters for reminders if both query parameters are present. If not, then this is an empty string.
         */
        const reminderQueryParams: string = !!appointmentKey
            && fromNotification ? encodeURIComponent(`?appointmentKey=${appointmentKey}&fromNotification=true${fromROReminder ? '&fromROReminder=true' : ''}`) : '';

        window.location.replace(`${process.env.REACT_APP_DSIM_BASE_URL}Authenticate?` +
            'scope=openid%20email&' +
            'response_type=code&' +
            `client_id=${process.env.REACT_APP_CLIENT_ID}&` +
            `code_challenge=${cryptoJS.enc.Base64url.stringify(hash)}&` +
            'code_challenge_method=S256&' +
            'response_mode=fragment&' +
            'max_mfa_age=604800&' +
            (prompt ? `prompt=${prompt}&` : '') +
            (dSIMUserId ? `login_hint=${dSIMUserId}&` : '') +
            `redirect_uri=${RunningAppType === AppType.Support ? process.env.REACT_APP_ADMIN_URL : `${process.env.REACT_APP_DASHBOARD_URL}${reminderQueryParams}`}`,
        );
    }, [RunningAppType, appointmentKey, fromNotification, fromROReminder]);

    const redirectToOnlinePaymentOnboarding = useCallback(() => {
        setActivating(true);
        window.location.replace(`${process.env.REACT_APP_API_BASE_URL}Reporting/ActivatePaymentAccountForDealerLink?dealerKey=${dealerKey}`);
    }, [dealerKey]);

    const getLoginErrorMessage = useCallback((errorMessage: LoginErrors | DashboardUserErrors, errorResponse?: string) => {
        if (errorMessage === LoginErrors.Other) {
            return (
                errorResponse !== undefined && (errorResponse in LoginErrors || errorResponse in DashboardUserErrors) ?
                    Localization.getString(errorResponse) ?? Localization.getString(errorResponse, Locales.en) :
                    errorResponse
            );
        }

        if (errorMessage === LoginErrors.AuthorizationError) {
            return Localization.formatString(Localization.AuthorizationError, errorResponse ?? '') as string;
        }

        return Localization.getString(errorMessage.toString()) ?? Localization.getString(errorMessage.toString(), Locales.en);
    }, [Localization]);

    const displayError = useCallback((errorMessage: LoginErrors | DashboardUserErrors, errorResponse?: string) => {
        if (RunningAppType === AppType.Support) {
            if (errorResponse || errorMessage !== LoginErrors.None) {
                setMessage({
                    message: getLoginErrorMessage(errorMessage, errorResponse) ?? '',
                    title: Localization.Error,
                    onClose: () => redirectToDSIM(),
                });
            }
        }
        else if (RunningAppType === AppType.Dashboard) {
            setErrorMessage(errorMessage);
            setErrorResponse(errorResponse);
            setLoading(false);
        }
    }, [RunningAppType, setMessage, getLoginErrorMessage, Localization.Error, redirectToDSIM]);

    const canUserLogin = useCallback((user: LoggedInUser) => {
        let error: LoginErrors = LoginErrors.None;

        if (
            (RunningAppType === AppType.Dashboard && !user.accessible_dealers.length) ||
            (RunningAppType === AppType.Dashboard
                && user.is_staff && !user.permissions.find((p) => p.permission === UserPermissions.SupportDashboard && p.permissionLevel === PermissionLevel.YesFull)) ||
            (RunningAppType === AppType.Support && !user.is_staff)
        ) {
            error = LoginErrors.AccountNoAccessError;
        }
        else if (user.is_locked) {
            error = LoginErrors.AccountLockedError;
        }
        else if (!user.is_active) {
            error = LoginErrors.AccountInactiveError;
        }

        if (error !== LoginErrors.None) {
            clearSessionStore();
            displayError(error);
            return false;
        }
        else {
            return true;
        }
    }, [RunningAppType, clearSessionStore, displayError]);

    const loginSuccessHandler = useCallback((data: LoggedInUser) => {
        if (canUserLogin(data)) {
            store.session.remove(SessionStorageKeys.CodeVerifier);
            login(data);
        }
    }, [login, canUserLogin]);

    const loginErrorHandler = useCallback((error: string) => {
        clearSessionStore();
        displayError(LoginErrors.Other, error);
    }, [displayError, clearSessionStore]);

    useEffect(() => {
        generateCodeVerifier();

        // If logged out due to the session expiring, display a message informing them of this.
        if (!isSessionValid) {
            displayError(LoginErrors.SessionExpiredError);
        }
        // If refreshing the page and we have a login token, display a loading screen while we fetch the logged in user data
        else if (store.session.has(SessionStorageKeys.Token) && store.session.has(SessionStorageKeys.LoginSession)) {
            api<LoggedInUser>(
                loginSuccessHandler,
                undefined,
                loginErrorHandler,
                undefined,
                loginErrorHandler,
            )
                .post('Login/LoggedInUser', store.session.get(SessionStorageKeys.LoginSession));
        }
        // If we have a dSIM auth code, attempt to get a login token
        else if (authorization.current) {
            window.location.hash = '';

            dSIMInstance.post('Token',
                'grant_type=authorization_code&' +
                `code=${authorization.current}&` +
                `client_id=${process.env.REACT_APP_CLIENT_ID}&` +
                `code_verifier=${codeVerifier.current}`,
            )
                .then((response) => {
                    if (response.data['access_token']) {
                        let token = JSON.parse(atob(response.data['access_token']))['token'];
                        store.session.set(SessionStorageKeys.Token, token);
                        store.session.set(SessionStorageKeys.RefreshToken, response.data['refresh_token']);
                        store.session.set(SessionStorageKeys.TokenType, LoginTokenTypes.DlrSecured);

                        api<LoggedInUser>(
                            loginSuccessHandler,
                            undefined,
                            (error: string) => {
                                /*
                                 * Display a more user friendly error message if the user tries to login with a dlrSecured
                                 * account that does not have a corresponding GoMoto account
                                 */
                                if (error === 'Request failed with status code 401') {
                                    error = LoginErrors.AccountNoAccessError;
                                }

                                loginErrorHandler(error);
                            },
                            undefined,
                            loginErrorHandler,
                        )
                            .get(`Login/DSIMLogin?location=${RunningAppType === AppType.Support ? LoginLocation.Support : LoginLocation.Dashboard}`);
                    }
                    else {
                        throw Error(Localization.NoAccessToken);
                    }
                })
                .catch((error) => {
                    clearSessionStore();
                    displayError(LoginErrors.AuthorizationError, error.response?.data?.error_description ?? error.message);
                });
        }
        // If a dSIM user ID was passed in, redirect to dlrSecured to complete authentication
        else if (ssoUser.current !== null) {
            redirectToDSIM(ssoUser.current, '');
        }
        // If redirecting to the online payment onboarding or needing to display the current status of the dealer's online payment account
        else if ((activatePaymentAccount || returnedFromOnboarding) && !!dealerKey) {
            if (activatePaymentAccount) {
                redirectToOnlinePaymentOnboarding();
            }
            else if (returnedFromOnboarding) {
                authInstance.get<DealerPaymentAccount>(`/Reporting/GetPaymentAccountStatusForDealer?dealerKey=${dealerKey}`)
                    .then((response) => {
                        if (response.data && response.data.paymentProvider === PaymentProvider.StripeCanada) {
                            setPaymentAccount(response.data);
                        }
                    })
                    .catch(() => {
                        displayError(LoginErrors.FailedToRetrievePaymentAccountStatusError);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        }
        // Start the login process
        else {
            // We force users of the Support site to authenticate user dlrSecured
            if (RunningAppType === AppType.Support) {
                redirectToDSIM();
            }
            // Not all Dashboard users use dlrSecured, so we present them with a login form
            else {
                setLoading(false);
            }
        }
    }, [
        dSIMInstance,
        redirectToDSIM,
        Localization,
        loginSuccessHandler,
        loginErrorHandler,
        api,
        displayError,
        RunningAppType,
        isSessionValid,
        generateCodeVerifier,
        clearSessionStore,
        activatePaymentAccount,
        returnedFromOnboarding,
        dealerKey,
        redirectToOnlinePaymentOnboarding,
        authInstance,
    ]);

    const otherLocaleOption = (locale === Locales.en ? Locales.fr : Locales.en) as Locales;
    const localeButtonDisplay = Localization.getString(LocalesFull[otherLocaleOption], otherLocaleOption) || Localization.getString(LocalesFull[otherLocaleOption], Locales.en);

    return (
        <div className={css.root}>
            <div className={css.container}>
                <div className={css.card}>
                    <GMCard>
                        <Formik
                            innerRef={formRef}
                            initialValues={initialValues}
                            validationSchema={Yup.object()
                                .shape({
                                    [nameof<LoginFormData>('username')]: Yup.string()
                                        .trim()
                                        .required(LocalizationHelpers.formatRequired(Localization.Username)),
                                    [nameof<LoginFormData>('password')]: Yup.string()
                                        .when([], {
                                            is: () => screenType === ScreenType.Password,
                                            then: (schema) => schema.trim()
                                                .required(LocalizationHelpers.formatRequired(Localization.Password)),
                                        }),
                                    [nameof<LoginFormData>('email')]: Yup.string()
                                        .when([], {
                                            is: () => screenType === ScreenType.dSIMEnrollment,
                                            then: (schema) => schema
                                                .trim()
                                                .required(LocalizationHelpers.formatRequired(Localization.EmailAddress))
                                                .matches(VALID_EMAIL_REGEX, Localization.NotAValidEmailAddress),
                                        }),
                                    [nameof<LoginFormData>('newPassword')]: Yup.string()
                                        .when([], {
                                            is: () => screenType === ScreenType.PasswordCreation,
                                            then: (schema) => schema
                                                .trim()
                                                .required(LocalizationHelpers.formatRequired(Localization.NewPassword)),
                                        }),
                                    [nameof<LoginFormData>('confirmNewPassword')]: Yup.string()
                                        .when([], {
                                            is: () => screenType === ScreenType.PasswordCreation,
                                            then: (schema) => schema
                                                .trim()
                                                .required(LocalizationHelpers.formatRequired(Localization.ConfirmPassword)),
                                        }),
                                })}
                            onSubmit={(values, { setSubmitting, setFieldValue }) => {
                                displayError(LoginErrors.None);

                                switch (screenType) {
                                    case ScreenType.Username:
                                        authInstance.put<DashboardUserLoginInfo>('Login/Info', JSON.stringify(values.username.trim()))
                                            .then((response) => {
                                                if (response.data.locked) {
                                                    displayError(LoginErrors.AccountLockedError);
                                                    setSubmitting(false);
                                                }
                                                else if (!response.data.active) {
                                                    displayError(LoginErrors.AccountInactiveError);
                                                    setSubmitting(false);
                                                }
                                                else if (response.data.dsimUserId && response.data.mfaEnabled) {
                                                    redirectToDSIM(response.data.dsimUserId);
                                                }
                                                else {
                                                    setScreenType(ScreenType.Password);
                                                    setSubmitting(false);
                                                }
                                            })
                                            .catch(() => {
                                                displayError(LoginErrors.UserNotFoundError);
                                                setSubmitting(false);
                                            });
                                        break;
                                    case ScreenType.Password:
                                        authInstance.post<LoggedInUser>('/Login', {
                                            Username: values.username.trim(),
                                            Password: values.password,
                                        })
                                            .then((response) => {
                                                const user: LoggedInUser = response.data;

                                                store.session.set(SessionStorageKeys.Token, user.token);
                                                store.session.set(SessionStorageKeys.RefreshToken, user.refresh_token);
                                                store.session.set(SessionStorageKeys.TokenType, LoginTokenTypes.GoMoto);

                                                if (canUserLogin(user)) {
                                                    updateUser(user);
                                                    setFieldValue(nameof<LoginFormData>('email'), user.email);

                                                    /**
                                                     * Force staff users (Reynolds associates) to setup dSIM
                                                     * MFA if not already done
                                                     */
                                                    if (user.is_staff && !user.dsim_userid) {
                                                        // TODO prevent errrors on blur prior to new submit?
                                                        setScreenType(ScreenType.dSIMEnrollment);
                                                        setSubmitting(false);
                                                    }
                                                    else if (user.temp_password) {
                                                        // TODO prevent errrors on blur prior to new submit?
                                                        setScreenType(ScreenType.PasswordCreation);
                                                        setSubmitting(false);
                                                    }
                                                    else {
                                                        store.session.remove(SessionStorageKeys.CodeVerifier);

                                                        /**
                                                         * If enrollment failed attempt to re-enroll them
                                                         */
                                                        if (!user.dsim_userid && !user.is_auth_user) {
                                                            const profile: DashboardUserProfile = {
                                                                email: user.email,
                                                                password: values.password,
                                                                lastName: user.lastname,
                                                                firstName: user.firstname,
                                                                phoneNumber: user.phone_number,
                                                                notifications: user.notifications,
                                                                defaultPage: user.default_page,
                                                                defaultSupportPage: user.default_support_page,
                                                                defaultDealer: user.default_dealer,
                                                                profilePicture: user.profile_picture,
                                                                pin: user.pin,
                                                            };

                                                            updateUserProfile(
                                                                profile,
                                                                (_) => login(user),
                                                                (error: string) => displayError(LoginErrors.Other, error),
                                                                undefined,
                                                                () => setSubmitting(false),
                                                            );
                                                        }
                                                        else {
                                                            login(user);
                                                        }
                                                    }
                                                }
                                                else {
                                                    setSubmitting(false);
                                                }
                                            })
                                            .catch((error: AxiosError<APIError>) => {
                                                if (error.response?.data?.message) {
                                                    displayError(LoginErrors.Other, error.response.data.message);
                                                }
                                                else {
                                                    displayError(LoginErrors.AccountCredentialsError);
                                                }

                                                setSubmitting(false);
                                            });
                                        break;
                                    case ScreenType.dSIMEnrollment:
                                    case ScreenType.PasswordCreation:
                                        if (screenType === ScreenType.PasswordCreation && values.newPassword !== values.confirmNewPassword) {
                                            displayError(LoginErrors.PasswordValidationError);
                                            setSubmitting(false);
                                        }
                                        else {
                                            updateUserProfile(
                                                {
                                                    email: values.email.trim(),
                                                    newPassword: values.newPassword,
                                                    password: values.password,
                                                    lastName: UserData.lastname,
                                                    firstName: UserData.firstname,
                                                    phoneNumber: UserData.phone_number,
                                                    notifications: UserData.notifications,
                                                    defaultPage: UserData.default_page,
                                                    defaultSupportPage: UserData.default_support_page,
                                                    defaultDealer: UserData.default_dealer,
                                                    profilePicture: UserData.profile_picture,
                                                    pin: UserData.pin,
                                                },
                                                (user) => login(user),
                                                (error: string) => displayError(LoginErrors.Other, error),
                                                undefined,
                                                () => setSubmitting(false),
                                            );
                                        }
                                        break;
                                }
                            }}
                        >
                            {({
                                handleSubmit,
                                isSubmitting,
                                resetForm,
                                validateForm,
                            }) => (
                                <>
                                    <GMCardBody noPadding>
                                        <div>
                                            <div className={css.header}>
                                                <img
                                                    src={LoginScreenLogo}
                                                    alt={'GoMoto'}
                                                    className={css.image}
                                                />
                                            </div>
                                            <div className={css.body}>
                                                {loading ?
                                                    <>
                                                        <div className={css.text}>
                                                            <Heading2Text>
                                                                {Localization.LoadingUserSettings}
                                                            </Heading2Text>
                                                        </div>
                                                        <div className={classNames(css.loadingSpinner, css.loadingScreen)}>
                                                            <GMSpinner />
                                                        </div>
                                                    </> :
                                                    <>
                                                        <div className={css.localeButtonContainer}>
                                                            <GMButton
                                                                buttonType={'secondary_button'}
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    localeDispatch({
                                                                        type: LocaleDispatchTypes.SetLocale,
                                                                        payload: otherLocaleOption,
                                                                    });
                                                                    store.local.set(LocalStorageKeys.BrowserLang, otherLocaleOption);

                                                                    window.requestAnimationFrame(() => formRef.current?.validateForm());
                                                                }}
                                                            >
                                                                {localeButtonDisplay}
                                                            </GMButton>
                                                        </div>
                                                        <div className={css.text}>
                                                            {paymentAccount ?
                                                                <div>
                                                                    {paymentAccount.paymentAccountDetailsSubmitted || paymentAccount.onlinePaymentsActive ?
                                                                        <>
                                                                            <div>
                                                                                <Heading2Text>
                                                                                    {Localization.YoureAllSet}
                                                                                </Heading2Text>
                                                                                <div className={classNames(css.instructionText, css.onlinePaymentInstructionText)}>
                                                                                    <div>
                                                                                        {Localization.YourStripeAccountIsActive}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                            <div className={css.paymentAccount}>
                                                                                <p>
                                                                                    <b>{Localization.AccountID}</b>
                                                                                    {' '}
                                                                                    {'|'}
                                                                                    {paymentAccount.paymentAccount}
                                                                                </p>
                                                                                <p>
                                                                                    <b>{Localization.AccountStatus}</b>
                                                                                    {' '}
                                                                                    {'|'}
                                                                                    {paymentAccount.paymentAccountStatus}
                                                                                </p>
                                                                            </div>
                                                                        </> :
                                                                        <>
                                                                            <div>
                                                                                <Heading2Text>
                                                                                    {Localization.AlmostThere}
                                                                                </Heading2Text>
                                                                                <div className={classNames(css.instructionText, css.onlinePaymentInstructionText)}>
                                                                                    <div>
                                                                                        {Localization.WeNeedSomeMoreInformation}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                            <p>
                                                                                <GMButton
                                                                                    buttonType={'primary_button'}
                                                                                    onClick={redirectToOnlinePaymentOnboarding}
                                                                                    disabled={activating}
                                                                                    fullWidth={isMobile}
                                                                                >
                                                                                    {Localization.ActivateAccount}
                                                                                </GMButton>
                                                                            </p>
                                                                        </>
                                                                    }
                                                                    <GMDivider variant={'thin'} orientation={'horizontal'} />
                                                                </div>
                                                                :
                                                                <Heading2Text>
                                                                    {Localization.PleaseLogIn}
                                                                </Heading2Text>
                                                            }
                                                        </div>
                                                        <Form>
                                                            <JSXSwitch>
                                                                <JSXCase condition={!paymentAccount && screenType === ScreenType.Username}>
                                                                    <div className={css.instructionText}>
                                                                        <div>
                                                                            {Localization.EnterYourUsernameToAccess}
                                                                        </div>
                                                                    </div>
                                                                </JSXCase>
                                                                <JSXCase condition={!paymentAccount && screenType === ScreenType.Password}>
                                                                    <div className={css.instructionText}>
                                                                        <div>
                                                                            {Localization.EnterYourPassword}
                                                                        </div>
                                                                    </div>
                                                                </JSXCase>
                                                                <JSXCase condition={!paymentAccount && screenType === ScreenType.dSIMEnrollment}>
                                                                    <div className={css.instructionText}>
                                                                        <div>
                                                                            {Localization.YouCurrentlyDontHaveMFASetup}
                                                                        </div>
                                                                    </div>
                                                                </JSXCase>
                                                                <JSXCase condition={!paymentAccount && screenType === ScreenType.PasswordCreation}>
                                                                    <div className={css.instructionText}>
                                                                        <div>
                                                                            {Localization.YouAreCurrentlyUsingATempPassword}
                                                                        </div>
                                                                    </div>
                                                                </JSXCase>
                                                            </JSXSwitch>
                                                            {errorMessage !== LoginErrors.None &&
                                                                <div className={css.formError}>
                                                                    <GMAlert type={'error'} text={getLoginErrorMessage(errorMessage, errorResponse) ?? ''} />
                                                                </div>
                                                            }
                                                            {isSubmitting ?
                                                                <div className={classNames(css.loadingSpinner, css.loadingForm)}>
                                                                    <GMSpinner />
                                                                </div>
                                                                :
                                                                <div>
                                                                    <JSXSwitch>
                                                                        <JSXCase condition={screenType === ScreenType.Username}>
                                                                            <GMFormTextField
                                                                                variant={'outlined'}
                                                                                label={Localization.Username}
                                                                                name={nameof<LoginFormData>('username')}
                                                                                maxLength={UserFieldLengths.Username}
                                                                                placeholder={Localization.Username}
                                                                                autofocus
                                                                                padding={'none'}
                                                                            />
                                                                        </JSXCase>
                                                                        <JSXCase condition={screenType === ScreenType.Password}>
                                                                            <GMFormPasswordField
                                                                                variant={'outlined'}
                                                                                label={Localization.Password}
                                                                                name={nameof<LoginFormData>('password')}
                                                                                maxLength={UserFieldLengths.Password}
                                                                                placeholder={Localization.Password}
                                                                                autofocus
                                                                                autoComplete={'current-password'}
                                                                                padding={'none'}
                                                                            />
                                                                        </JSXCase>
                                                                        <JSXCase condition={screenType === ScreenType.dSIMEnrollment}>
                                                                            <GMFormEmailField
                                                                                variant={'outlined'}
                                                                                label={Localization.Email}
                                                                                name={nameof<LoginFormData>('email')}
                                                                                maxLength={UserFieldLengths.Email}
                                                                                placeholder={Localization.Email}
                                                                                autofocus
                                                                                padding={'none'}
                                                                            />
                                                                        </JSXCase>
                                                                        <JSXCase condition={screenType === ScreenType.PasswordCreation}>
                                                                            <GMFormPasswordField
                                                                                variant={'outlined'}
                                                                                label={Localization.NewPassword}
                                                                                name={nameof<LoginFormData>('newPassword')}
                                                                                maxLength={UserFieldLengths.Password}
                                                                                placeholder={Localization.NewPassword}
                                                                                autofocus
                                                                                autoComplete={'new-password'}
                                                                                padding={'dense'}
                                                                            />
                                                                            <GMFormPasswordField
                                                                                variant={'outlined'}
                                                                                label={Localization.ConfirmPassword}
                                                                                name={nameof<LoginFormData>('confirmNewPassword')}
                                                                                maxLength={UserFieldLengths.Password}
                                                                                placeholder={Localization.ConfirmPassword}
                                                                                autoComplete={'new-password'}
                                                                                padding={'none'}
                                                                            />
                                                                        </JSXCase>
                                                                    </JSXSwitch>
                                                                </div>
                                                            }
                                                        </Form>
                                                    </>
                                                }
                                            </div>
                                        </div>
                                    </GMCardBody>
                                    {!loading && !isSubmitting ?
                                        <GMCardFooter>
                                            <GMButtonList
                                                name={'login'}
                                                rightButtons={[
                                                    screenType !== ScreenType.Username ?
                                                        <GMButton
                                                            buttonType={'secondary_button'}
                                                            onClick={() => {
                                                                resetForm();
                                                                displayError(LoginErrors.None);
                                                                clearSessionStore();
                                                                setScreenType(ScreenType.Username);
                                                            }}
                                                            fullWidth={isMobile}
                                                        >
                                                            {Localization.Cancel}
                                                        </GMButton> : null,
                                                    <GMButton
                                                        buttonType={'primary_button'}
                                                        onClick={() => handleSubmit()}
                                                        submit
                                                        fullWidth={isMobile}
                                                    >
                                                        {Localization.Submit}
                                                    </GMButton>,
                                                ]}
                                            />
                                        </GMCardFooter> : null
                                    }
                                </>
                            )}
                        </Formik>
                    </GMCard>
                </div>
            </div>
        </div>
    );
};
